import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { UtilityService } from '@eforall/common';
import { AngularFormDialogData, DialogService, GridContentWidget } from '@eforall/dashboard';
import { Note, NotecategoryId, NoteUpsertValues } from '@interfaces';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { StaticDataService } from '../../../services';
import { ExplorerService } from '../../explorer.service';
import { getSetup } from "./grid/get-setup";

export interface BaseService {
	explorer: ExplorerService,
	reloadData: () => void,
}


@Component({
	selector: 'explorer-notes-part',
	standalone: true,
	imports: [
		TabStripModule,
		GridContentWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './notes.part.html'
})

export class ExplorerNotesPart {

	#util = inject(UtilityService);
	#dialog = inject(DialogService);
	#staticData = inject(StaticDataService);
	#explorer = inject(ExplorerService);

	service = input.required<BaseService>();
	notes = input.required<Note[]>();
	// Exactly one note parent is allowed (person or venture); the opposite key is kept as undefined only for safe property reads.
	notesParent = input.required<{ personId: number, accVentureId?: undefined } | { accVentureId: number, personId?: undefined }>();

	public setup = () => getSetup(this.notesParent().accVentureId ? 'acc-venture' : 'person', this.#util, this.service(), this.#dialog, this.#staticData, this.openNotesDialog);

	openNotesDialog = async (note?: Note) => {

		const contactMethodOptions = computed(() => {
			return this.#staticData.notecategorys.all.map(v => ({
				value: v.id,
				text: v.name,
				icon: v.id === NotecategoryId.RedFlag ? 'red fa-flag fas fa-fw text-danger' : 'far fa-sticky-note fa-fw',
			}));
		});

		const dialogData: AngularFormDialogData<NoteUpsertValues> = {
			header: note ? 'Edit Note' : 'Add Note',
			fields: [
				{
					kind: 'select',
					formFieldName: 'noteCategoryId',
					label: 'Category',
					initialValue: computed(() => note?.noteCategoryId),
					options: contactMethodOptions,
					validations: {
						isRequired: signal(true),
					}
				},
				{
					kind: 'text',
					formFieldName: 'subject',
					label: 'Subject',
					initialValue: computed(() => note?.subject),
					fixCase: 'sentence-case',
					validations: {
						isRequired: signal(true),
					}
				},
				{
					kind: 'text',
					formFieldName: 'note',
					label: 'Note',
					initialValue: computed(() => note?.note),
					fixCase: 'sentence-case',
					isMultiLine: true,
					validations: {
						isRequired: signal(true),
						minLength: 3,
					}
				}
			],
			onSave: async (values: NoteUpsertValues) => {
				if (note) {
					await this.#explorer.func.note.updateNote({ noteId: note.noteId, note: values });
				} else {
					await this.#explorer.func.note.addNote({ personId: this.notesParent().personId, accVentureId: this.notesParent().accVentureId, note: values });
				}
				await this.service().reloadData();
			}
		}
		await this.#dialog.showAngularForm(dialogData, 400, 450);

	}

}